// Auto-generated lightweight class for Attribute
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { AttributeReadAPI, AttributeWriteAPI, AttributeReadAPISchema, AttributeWriteAPISchema } from '../types/attribute';
import { ModelRegistry } from '../../registries/model-registry';
import { AtScope, AttributeInWorkspace, AttributeValueOfAttribute, CatalogAt, HasAttribute, HasAttributeValue, HasDerivedValue, IslandHoldsPin, Mentions } from '../../relationships/relationship';

import { SecurityScope } from '../../shared/api/securityscope';
import { Workspace } from '../../base/api/workspace';
import { AttributeValue } from './attributevalue';
import { DerivedValue } from './derivedvalue';
import { Expression } from './expression';
import { IslandPin } from './islandpin';



export interface Attribute extends Partial<AttributeReadAPI> {}
export class Attribute extends NeoNodeBase<AttributeReadAPI, AttributeWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "Attribute";
  static readonly readSchema = AttributeReadAPISchema;
  static readonly writeSchema = AttributeWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    catalog_scope: { target: "SecurityScope", cardinality: "one", isParent: false, relClass: "CatalogAt", direction: "out" },
    workspace: { target: "Workspace", cardinality: "one", isParent: false, relClass: "AttributeInWorkspace", direction: "out" },
    values: { target: "AttributeValue", cardinality: "many", isParent: false, relClass: "AttributeValueOfAttribute", direction: "in" },
    attributes: { target: "Attribute", cardinality: "many", isParent: false, relClass: "HasAttribute", direction: "out" },
    attribute_values: { target: "AttributeValue", cardinality: "many", isParent: false, relClass: "HasAttributeValue", direction: "out" },
    derived_values: { target: "DerivedValue", cardinality: "many", isParent: false, relClass: "HasDerivedValue", direction: "out" },
    mentioned_in_expressions: { target: "Expression", cardinality: "many", isParent: false, relClass: "Mentions", direction: "in" },
    island_pins: { target: "IslandPin", cardinality: "many", isParent: false, relClass: "IslandHoldsPin", direction: "out" },
  };

  protected get modelName(): string {
    return 'Attribute';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return Attribute.label;
  }


  constructor(data: Partial<AttributeWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: AttributeReadAPI, loadContext?: Record<string, any>): Attribute {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { catalog_scope, security_scope, workspace, values, attributes, attribute_values, derived_values, mentioned_in_expressions, island_pins, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = AttributeReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, catalog_scope: catalog_scope, security_scope: security_scope, workspace: workspace, values: values, attributes: attributes, attribute_values: attribute_values, derived_values: derived_values, mentioned_in_expressions: mentioned_in_expressions, island_pins: island_pins };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return Attribute.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadOneRelationship(processed, 'catalog_scope', 'SecurityScope', ModelRegistry);
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadOneRelationship(processed, 'workspace', 'Workspace', ModelRegistry);
    loadManyRelationship(processed, 'values', 'AttributeValue', ModelRegistry);
    loadManyRelationship(processed, 'attributes', 'Attribute', ModelRegistry);
    loadManyRelationship(processed, 'attribute_values', 'AttributeValue', ModelRegistry);
    loadManyRelationship(processed, 'derived_values', 'DerivedValue', ModelRegistry);
    loadManyRelationship(processed, 'mentioned_in_expressions', 'Expression', ModelRegistry);
    loadManyRelationship(processed, 'island_pins', 'IslandPin', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new Attribute(processed as any);

    return instance;
  }


  // Relationship manager for 'canonical_unit' - Python ORM style (cached)
  get canonical_unit(): RelationshipManager<this, any> {
    return this.getOrCreateRelationshipManager('canonical_unit', () => {
      return new RelationshipManager(this, 'canonical_unit', 'many');
    });
  }

  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'catalog_scope' - Python ORM style (cached)
  get catalog_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('catalog_scope', () => {
      return new RelationshipManager(this, 'catalog_scope', 'one', CatalogAt as typeof Relationship);
    });
  }

  // Relationship manager for 'workspace' - Python ORM style (cached)
  get workspace(): RelationshipManager<this, Workspace> {
    return this.getOrCreateRelationshipManager('workspace', () => {
      return new RelationshipManager(this, 'workspace', 'one', AttributeInWorkspace as typeof Relationship);
    });
  }

  // Relationship manager for 'values' - Python ORM style (cached)
  get values(): RelationshipManager<this, AttributeValue> {
    return this.getOrCreateRelationshipManager('values', () => {
      return new RelationshipManager(this, 'values', 'many', AttributeValueOfAttribute as typeof Relationship);
    });
  }

  // Relationship manager for 'attributes' - Python ORM style (cached)
  get attributes(): RelationshipManager<this, Attribute> {
    return this.getOrCreateRelationshipManager('attributes', () => {
      return new RelationshipManager(this, 'attributes', 'many', HasAttribute as typeof Relationship);
    });
  }

  // Relationship manager for 'attribute_values' - Python ORM style (cached)
  get attribute_values(): RelationshipManager<this, AttributeValue> {
    return this.getOrCreateRelationshipManager('attribute_values', () => {
      return new RelationshipManager(this, 'attribute_values', 'many', HasAttributeValue as typeof Relationship);
    });
  }

  // Relationship manager for 'derived_values' - Python ORM style (cached)
  get derived_values(): RelationshipManager<this, DerivedValue> {
    return this.getOrCreateRelationshipManager('derived_values', () => {
      return new RelationshipManager(this, 'derived_values', 'many', HasDerivedValue as typeof Relationship);
    });
  }

  // Relationship manager for 'mentioned_in_expressions' - Python ORM style (cached)
  get mentioned_in_expressions(): RelationshipManager<this, Expression> {
    return this.getOrCreateRelationshipManager('mentioned_in_expressions', () => {
      return new RelationshipManager(this, 'mentioned_in_expressions', 'many', Mentions as typeof Relationship);
    });
  }

  // Relationship manager for 'island_pins' - Python ORM style (cached)
  get island_pins(): RelationshipManager<this, IslandPin> {
    return this.getOrCreateRelationshipManager('island_pins', () => {
      return new RelationshipManager(this, 'island_pins', 'many', IslandHoldsPin as typeof Relationship);
    });
  }

}
